import { FormikCheckBox, FormikInputGroup, FormikSelect, FormikSlider } from '@n2-common/ui';
import {
    accommodationStatusCodesFi,
    type AccomodationStatusValue,
    employmentStatusCodeFi,
    martialStatusValues,
    trueFalseValues,
    type EmploymentStatusCode,
} from '@n2-common/uiFieldValues/applicationFormFields';
import { amount, amountNotNull } from '@n2-common/validation/common/amount';
import { TFunction } from 'i18next';
import { FinnishAmountInput as AmountInput, EmailInput, FinnishMobilePhoneNumberInput, FinnishSsnInput } from '@n2-common/ui/CustomInput';
import { stringRequired } from '@n2-common/validation/common/string';
import { emailRequired } from '@n2-common/validation/common/email';
import { LoanFiApplicationInputModel as LoanFiApplicationInputModelType } from '@n2-common/serverGeneratedTypings/loanFiApplicationInputModel';
import { mobileRequired } from '@n2-common/validation/fi/phoneNumber';
import { getNationalIdValidation as getNationalIdValidation } from '@n2-common/validation/fi/nationalId';
import { addPleaseSelectOption } from '@n2-common/components/MultiStageOnePageWizard';
import * as Yup from 'yup';
import { LoanFiApplicantData as LoanFiApplicantDataType } from '@n2-common/serverGeneratedTypings/loanFiApplicantData';
import style from '../style.module.scss';
import React from 'react';
import type { StageField, WizardStage } from '@n2-common/components/MultiStageOnePageWizard/types';
import { hideRefinanceDebt, specifyAccomodationCost } from './helpers';
import { type ConfigType } from './helpers';
import { ConsentType } from '@n2-common/uiFieldValues/consents';
import AmountVisualizer, { CliFieldConfig } from '../AmountVisualizer';
import { formatWithCurrencyNoDecimals } from '@n2-common/lib/money';

export type FinishApplicant = Override<
    PropertyNaming<LoanFiApplicantDataType>,
    {
        accommodationType: AccomodationStatusValue | '';
        employmentStatus: EmploymentStatusCode | '';
    }
>;

export type FinishLoanApplication = Override<
    PropertyNaming<LoanFiApplicationInputModelType>,
    {
        coApplicant: FinishApplicant | null;
        mainApplicant: FinishApplicant;
        consents: ConsentType;
        gpid: Guid;
        coSignerGpid?: Guid;
    }
>;

export type FinishLoanApplicationCleaned = Override<
    FinishLoanApplication,
    {
        consents: string[];
    }
>;

export type ApplicantType = 'mainApplicant' | 'coApplicant';

export const initialApplicantValue: FinishApplicant = {
    email: '',
    phonenumber: '',
    nationalId: '',
    maritalStatus: '',
    employmentStatus: '',
    monthlyGrossIncome: '',
    accommodationType: '',
    accommodationCost: '',
    hasCar: '',
    monthlyIncome: '',
    extraEarningMonthly: '',
    mortgage: '',
    studentLoan: '',
    carLoan: '',
};

export const initialValues: FinishLoanApplication = {
    desiredCredit: '',
    refinanceDebt: false,
    hasCoApplicant: false,
    coApplicant: initialApplicantValue,
    mainApplicant: initialApplicantValue,
    gpid: '',
    coSignerGpid: '',
    gAClientRef: '',
    consents: {},
    tracking: {},
};

export const amountBlock = (t: TFunction, config: ConfigType): WizardStage<FinishLoanApplication> => ({
    legend: '',
    sectionNameForTracking: 'Amount',
    fields: [
        {
            name: 'desiredCredit',
            validation: amount(config.minLoanAmount, config.maxLoanAmount, t),
            componentProps: {
                label: 'Ilmoita haluamasi luottoraja',
            },
            Component: AmountInput,
        },
        {
            name: 'refinanceDebt',
            hidden: (form) => hideRefinanceDebt(form.desiredCredit, config),
            validation: Yup.bool(),
            componentProps: {
                label: 'Haluan jälleenrahoittaa olemassaolevia luottoja',
            },
            Component: FormikCheckBox,
        },
    ],
});
export const amountBlockCli = (t: TFunction, config: CliFieldConfig): WizardStage<FinishLoanApplication> => {
    const minLimit = config.minIncrement;

    return {
        legend: '',
        sectionNameForTracking: 'Amount',
        fields: [
            {
                name: 'desiredCredit',
                validation: amount(minLimit, config.maxIncrement, t),
                componentProps: {
                    label: t<string>('customer-loan:forms.application.desiredCredit', {
                        minLimit: formatWithCurrencyNoDecimals(minLimit, 'FI'),
                        maxLimit: formatWithCurrencyNoDecimals(config.maxIncrement, 'FI'),
                    }),
                },
                Component: config.amountAsSlider
                    ? (props) =>
                          React.createElement(FormikSlider, {
                              ...props,
                              options: { min: minLimit, max: config.maxIncrement },
                          })
                    : AmountInput,
            },
            {
                name: 'desiredCreditView',
                validation: Yup.bool().nullable(),
                componentProps: {
                    label: '',
                },
                Component: (props) =>
                    React.createElement(AmountVisualizer, {
                        country: 'FI',
                        ...config,
                        ...props,
                    }),
            },
        ],
    };
};

export const firstBlock = (t: TFunction, isCli = false): WizardStage<FinishLoanApplication> => ({
    legend: (values) => `Hakijan tiedot ${values.hasCoApplicant ? ' päähakija' : ''}`,
    sectionNameForTracking: `contact information`,
    fields: [...firstBlockFields(t, 'mainApplicant', isCli)],
});

export const firstBlockCoAplicant = (t: TFunction, isCli = false): WizardStage<FinishLoanApplication> => ({
    legend: `Personal info coApplicant`,
    hidden: (values) => !values.hasCoApplicant,
    sectionNameForTracking: 'Personal info coApplicant',
    fields: [...firstBlockFields(t, 'coApplicant', isCli)],
});

export const secondBlock = (t: TFunction, applicant: ApplicantType): WizardStage<FinishLoanApplication> => ({
    hidden: applicant === 'coApplicant' ? (values) => !values.hasCoApplicant : undefined,
    legend: `Taloudellinen tilanne${applicant === 'coApplicant' ? ' hakijakaveri' : ''}`,
    sectionNameForTracking: `Economical information${applicant === 'coApplicant' ? ' coApplicant' : ''}`,
    fields: [
        {
            name: `${applicant}.maritalStatus`,
            validation: stringRequired(t),
            componentProps: {
                label: 'Siviilisääty',
            },
            Component: (props) =>
                React.createElement(FormikSelect, {
                    ...props,
                    options: addPleaseSelectOption(martialStatusValues(t), t),
                }),
        },
        {
            name: `${applicant}.monthlyGrossIncome`,
            validation: amount(0, Number.MAX_SAFE_INTEGER, t),
            componentProps: {
                label: 'Kuukausitulot (brutto)',
            },
            Component: (props) =>
                React.createElement(AmountInput, {
                    ...props,
                    helptext:
                        'Kuukausitulosi ennen veroja. Mikäli saat palkkaa useammalta työnantajalta, voit yhdistää summan. Vuokratulot, tai lapsilisät eivät sisälly tähän kenttään.',
                }),
        },
        {
            name: `${applicant}.monthlyIncome`,
            validation: amount(0, Number.MAX_SAFE_INTEGER, t),
            componentProps: {
                label: 'Kuukausitulot (netto)',
            },
            Component: (props) =>
                React.createElement(AmountInput, {
                    ...props,
                    helptext:
                        'Kuukausitulosi verojen jälkeen. Mikäli saat palkkaa useammalta työnantajalta, voit yhdistää summan. Vuokratulot, tai lapsilisät eivät sisälly tähän kenttään.',
                }),
        },
        {
            name: `${applicant}.extraEarningMonthly`,
            validation: amount(0, Number.MAX_SAFE_INTEGER, t),
            componentProps: {
                label: 'Kuukausittainen pääomatulo bruttona (esim. vuokratulo)',
            },
            Component: (props) =>
                React.createElement(AmountInput, {
                    ...props,
                    helptext:
                        'Kuukausittainen pääomatulosi ennen veroja. Mikäli saat esimerkiksi vuokratuloa, voit antaa summan tähän kenttään. Voit yhdistää kaikki pääomatulosi tähän kentään.',
                }),
        },
        {
            name: `${applicant}.employmentStatus`,
            validation: stringRequired(t),
            componentProps: {
                label: 'Tämänhetkinen työtilanteesi',
            },
            Component: (props) =>
                React.createElement(FormikSelect, {
                    //
                    options: addPleaseSelectOption(employmentStatusCodeFi, t),
                    ...props,
                }),
        },
    ],
});

export const thirdBlock = (t: TFunction, applicant: ApplicantType, isCli: boolean): WizardStage<FinishLoanApplication> => ({
    hidden: applicant === 'coApplicant' ? (values) => !values.hasCoApplicant : undefined,
    legend: `Muut kulut${applicant === 'coApplicant' ? ' hakijakaveri' : ''}`,
    sectionNameForTracking: `Debt information${applicant === 'coApplicant' ? ' coApplicant' : ''}`,
    fields: [...thirdBlockFields(t, applicant, isCli)],
});

function* thirdBlockFields(t: TFunction, applicant: ApplicantType, isCli: boolean): Generator<StageField<FinishLoanApplication>> {
    yield {
        name: `${applicant}.accommodationType`,
        validation: stringRequired(t),
        componentProps: {
            label: 'Asumismuoto',
        },
        Component: (props) => React.createElement(FormikSelect, { ...props, options: addPleaseSelectOption(accommodationStatusCodesFi, t) }),
    };
    yield {
        name: `${applicant}.accommodationCost`,
        hidden: (values) => !specifyAccomodationCost(values[applicant]?.accommodationType),
        validation: Yup.number().when('accommodationType', {
            is: (val: FinishApplicant['accommodationType']) => specifyAccomodationCost(val),
            then: () => amountNotNull(t),
            otherwise: () => Yup.number().nullable(),
        }),
        componentProps: {
            label: '* Sinun osuutesi vuokrasta / yhtiövastikkeesta',
            helptext: 'Kirjoita tähän kenttään oma osuutesi vuokrasta tai yhtiövastikkeesta (sis. hoito- ja rahoitusvastikkeen) kuukaudessa',
        },
        Component: AmountInput,
    };
    yield {
        name: `${applicant}.mortgage`,
        validation: amountNotNull(t),
        componentProps: {
            label: 'Sinun osuutesi asuntolainasta',
            helptext: 'Kirjoita tähän kenttään oma osuutesi voimassa olevasta asuntolainasta',
        },
        Component: AmountInput,
    };
    yield {
        name: `${applicant}.studentLoan`,
        validation: amountNotNull(t),
        componentProps: {
            label: 'Valtion takaama opintolaina',
            helptext: 'Kirjoita tähän kenttään tällä hetkellä voimassa olevien opintolainojesi jäljellä oleva kokonaissumma',
        },
        Component: AmountInput,
    };
    yield {
        name: `${applicant}.carLoan`,
        validation: amountNotNull(t),
        componentProps: {
            label: 'Sinun osuutesi ajoneuvolainasta',
            helptext: 'Kirjoita tähän kenttään oma osuutesi voimassa olevasta ajoneuvolainasta',
        },
        Component: AmountInput,
    };
    yield {
        name: `${applicant}.hasCar`,
        validation: stringRequired(t),
        componentProps: {
            label: 'Onko sinulla oma tai leasing-auto',
        },
        Component: (props) =>
            React.createElement(FormikInputGroup, {
                ...props,
                type: 'radio',
                options: trueFalseValues(t),
                className: style.radioButtons,
                horisontal: true,
            }),
    };
    if (isCli) {
        yield {
            name: `${applicant}View`,
            validation: Yup.boolean()
                .nullable()
                .required(t<string>('validation:consent.required'))
                .oneOf([true], t<string>('validation:consent.required')),
            Component: FormikCheckBox,
            componentProps: { label: t<string>('customer-loan:forms.application.confirmFinancialSituation') },
        };
    }
}

function* firstBlockFields(t: TFunction, applicant: ApplicantType, isCli = false): Generator<StageField<FinishLoanApplication>> {
    yield {
        name: `${applicant}.email`,
        validation: emailRequired(t),
        componentProps: {
            label: 'Sähköpostiosoite',
        },
        Component: EmailInput,
    };
    yield {
        name: `${applicant}.phonenumber`,
        validation: mobileRequired(),
        componentProps: {
            label: 'Matkapuhelin',
            helptext: t<string>('validation:phoneNumber.format'),
        },
        Component: FinnishMobilePhoneNumberInput,
    };
    if (!isCli) {
        yield {
            name: `${applicant}.nationalId`,
            validation: getNationalIdValidation(),
            componentProps: {
                label: 'Henkilötunnus',
            },
            Component: FinnishSsnInput,
        };
    }
    if (applicant == 'coApplicant') {
        yield {
            name: `coApplicant.maritalStatus`,
            validation: stringRequired(t),
            componentProps: {
                label: 'Civilstånd',
            },
            Component: (props) => React.createElement(FormikSelect, { ...props, options: addPleaseSelectOption(martialStatusValues(t), t) }),
        };
    }
    if (isCli) {
        yield {
            name: `${applicant}View`,
            validation: Yup.boolean()
                .nullable()
                .required(t<string>('validation:consent.required'))
                .oneOf([true], t<string>('validation:consent.required')),
            Component: FormikCheckBox,
            componentProps: { label: t<string>('customer-loan:forms.application.confirmPersonalInformation') },
        };
    }
}
